import { get } from 'lodash-es';
import type { Allocation } from '../../../../../types';
import type { EntityAdminRecord } from '../../../types';
import { AllocationsSelectorContent } from './AllocationsSelectorContent/AllocationsSelectorContent';
import { DividerContent } from './DividerContent';
import { DropdownContent } from './DropdownContent';
import { InlineToggleContent } from './InlineToggleContent';
import { InputContent } from './InputContent';
import { MultiSelectDropdownContent } from './MultiSelectDropdownContent';
import { registerDrawerContentRenderer } from './registry';
import { SymbolSelectorContent } from './SymbolSelectorContent';
import { ToggleHorizontalContent } from './ToggleHorizontalContent';

// Register standard drawer content renderers
export function registerDefaultDrawerContentRenderers(): void {
  // Register divider content
  registerDrawerContentRenderer('divider', {
    renderer: props => {
      if (props.drawerOption.type === 'divider') {
        return <DividerContent title={props.drawerOption.title} />;
      }
      return null;
    },
  });

  // Register input content
  registerDrawerContentRenderer('input', {
    renderer: props => {
      if (props.drawerOption.type === 'input') {
        return (
          <InputContent
            drawerOption={props.drawerOption}
            form={props.form}
            isEditing={props.isEditing}
            onFormUpdate={props.onFormUpdate}
            inheritedValue={props.inheritedValue}
          />
        );
      }
      return null;
    },
  });

  registerDrawerContentRenderer('inputNumeric', {
    renderer: props => {
      if (props.drawerOption.type === 'inputNumeric') {
        return (
          <InputContent
            drawerOption={props.drawerOption}
            form={props.form}
            isEditing={props.isEditing}
            onFormUpdate={props.onFormUpdate}
            inheritedValue={props.inheritedValue}
          />
        );
      }
      return null;
    },
  });

  // Register dropdown content
  registerDrawerContentRenderer('dropdown', {
    renderer: props => {
      if (props.drawerOption.type === 'dropdown') {
        return (
          <DropdownContent
            drawerOption={props.drawerOption}
            form={props.form}
            isEditing={props.isEditing}
            onFormUpdate={props.onFormUpdate}
            inheritedValue={props.inheritedValue}
          />
        );
      }
      return null;
    },
  });

  registerDrawerContentRenderer<EntityAdminRecord, string[]>('dropdownMulti', {
    isValueProvided: (form, formField) => {
      const value = get(form, formField);
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return false;
    },
    renderer: props => {
      if (props.drawerOption.type === 'dropdownMulti') {
        return (
          <MultiSelectDropdownContent
            drawerOption={props.drawerOption}
            form={props.form}
            isEditing={props.isEditing}
            onFormUpdate={props.onFormUpdate}
            inheritedValue={props.inheritedValue}
          />
        );
      }
      return null;
    },
  });

  registerDrawerContentRenderer<EntityAdminRecord, unknown>('custom', {
    renderer: props => {
      if (props.drawerOption.type === 'custom') {
        return props.drawerOption.renderer({
          drawerOption: props.drawerOption,
          form: props.form,
          isEditing: props.isEditing,
          onFormUpdate: props.onFormUpdate,
        });
      }
      return null;
    },
  });

  registerDrawerContentRenderer('toggle', {
    renderer: props => {
      if (props.drawerOption.type === 'toggle') {
        return (
          <ToggleHorizontalContent
            drawerOption={props.drawerOption}
            form={props.form}
            isEditing={props.isEditing}
            onFormUpdate={props.onFormUpdate}
          />
        );
      }
      return null;
    },
  });

  registerDrawerContentRenderer('inlineToggle', {
    renderer: props => {
      if (props.drawerOption.type === 'inlineToggle') {
        return (
          <InlineToggleContent drawerOption={props.drawerOption} form={props.form} onFormUpdate={props.onFormUpdate} />
        );
      }
      return null;
    },
  });

  registerDrawerContentRenderer<EntityAdminRecord, Allocation[]>('allocations', {
    renderer: ({ drawerOption, ...props }) => {
      if (drawerOption.type === 'allocations') {
        return <AllocationsSelectorContent drawerOption={drawerOption} {...props} />;
      }
      return null;
    },
  });

  registerDrawerContentRenderer('symbolSelector', {
    renderer: props => {
      if (props.drawerOption.type === 'symbolSelector') {
        return (
          <SymbolSelectorContent
            drawerOption={props.drawerOption}
            form={props.form}
            isEditing={props.isEditing}
            onFormUpdate={props.onFormUpdate}
            inheritedValue={props.inheritedValue}
          />
        );
      }
      return null;
    },
  });
}
